import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useGetMe, useSyncCheckoutStatus } from '@practium/features/api';
import { authService, useAuth } from '@practium/features/auth';

const MAX_POLL_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;
const ACTIVE_UPGRADE_STATUSES = new Set(['pending', 'failed', 'canceled']);

function resolveActiveOutcome(me?: {
	subscription?: any;
	upgradeSubscription?: any;
} | null) {
	const upgrade = me?.upgradeSubscription;
	const upgradeStatus = upgrade?.paymentStatus ?? undefined;

	if (upgradeStatus && ACTIVE_UPGRADE_STATUSES.has(upgradeStatus)) {
		return { subscription: upgrade, paymentStatus: upgradeStatus };
	}

	return {
		subscription: me?.subscription,
		paymentStatus: me?.subscription?.paymentStatus ?? undefined,
	};
}

function isUnauthorizedQueryError(error: unknown) {
	return axios.isAxiosError(error) && error.response?.status === 401;
}

export const usePaymentOutcome = () => {
	const { isLoading: isAuthLoading } = useAuth();
	const hasToken = authService.isAuthenticated();
	const {
		data: me,
		isLoading,
		isError,
		error,
		refetch,
	} = useGetMe({}, { enabled: !isAuthLoading && hasToken, retry: false });
	const { mutateAsync: syncCheckoutStatus } = useSyncCheckoutStatus();
	const [attempts, setAttempts] = useState(0);
	const hasSynced = useRef(false);

	const { subscription, paymentStatus } = resolveActiveOutcome(me);
	const isPending = paymentStatus === 'pending';
	const isSessionError = (!isAuthLoading && !hasToken) || isUnauthorizedQueryError(error);

	useEffect(() => {
		if (!isPending || attempts >= MAX_POLL_ATTEMPTS) return;
		const timeout = setTimeout(() => {
			refetch();
			setAttempts((prev) => prev + 1);
		}, POLL_INTERVAL_MS);
		return () => clearTimeout(timeout);
	}, [isPending, attempts, refetch]);

	useEffect(() => {
		if (!isPending || !me || hasSynced.current) return;
		hasSynced.current = true;
		syncCheckoutStatus()
			.then(() => refetch())
			.catch(() => {});
	}, [isPending, me, syncCheckoutStatus, refetch]);

	useEffect(() => {
		if (!isPending) hasSynced.current = false;
	}, [isPending]);

	const isResolving =
		isAuthLoading || (hasToken && isLoading) || (isPending && attempts < MAX_POLL_ATTEMPTS);
	const timedOut = isPending && attempts >= MAX_POLL_ATTEMPTS;

	const checkAgain = () => {
		hasSynced.current = false;
		setAttempts(0);
		if (hasToken) refetch();
	};

	return {
		me,
		subscription,
		paymentStatus,
		isResolving,
		timedOut,
		isError: isError && !isSessionError,
		isSessionError,
		checkAgain,
		refetch,
	};
};
